/**
 * PaymasterContextBanner — Sprint 4 UX surface.
 *
 * Renders a permanent banner under the navbar when the current
 * company is in a cross-company payroll relationship — either
 * operated by a paymaster, or operating one or more sister
 * companies' payrolls.
 *
 * The council mandate is "make cross-tenant context unmistakable".
 * Sticky, role-coloured, with a quick link to the management page.
 */

import { Alert, AlertTitle, Box, Chip, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { groupPayrollApi } from '@/api/endpoints/groupPayroll';
import { useAuthStore } from '@/stores/authStore';

const STALE_30S = 30 * 1000;

export default function PaymasterContextBanner() {
  const activeCompanyId = useAuthStore((s) => s.activeCompanyId);
  const navigate = useNavigate();

  const ctxQ = useQuery({
    queryKey: ['group-payroll', 'context', activeCompanyId],
    queryFn: () => groupPayrollApi.context(),
    staleTime: STALE_30S,
    enabled: !!activeCompanyId,
    // Best-effort — never poison the shell if the endpoint 403s for
    // users without payroll.read.
    retry: false,
  });

  const ctx = ctxQ.data;
  if (!ctx || !ctx.feature_enabled || ctx.role === 'standalone') return null;

  const isOperated = ctx.role === 'operated';
  const severity = isOperated ? 'warning' : 'info';
  const title = isOperated
    ? `Your payroll is operated by ${ctx.paymaster_company_name ?? 'a paymaster'}`
    : `You are running payroll for ${ctx.operated_companies.length} sister ${ctx.operated_companies.length === 1 ? 'company' : 'companies'}`;

  const inboxBadges: { label: string; count: number }[] = [];
  if (ctx.pending_invites_count > 0)
    inboxBadges.push({ label: 'pending invites', count: ctx.pending_invites_count });
  if (ctx.pending_approvals_count > 0)
    inboxBadges.push({ label: 'pending approvals', count: ctx.pending_approvals_count });

  return (
    <Alert
      severity={severity}
      variant="outlined"
      sx={{
        borderRadius: 0,
        borderLeft: 0,
        borderRight: 0,
        py: 0.5,
        px: 2,
        '& .MuiAlert-message': { width: '100%', py: 0 },
      }}
      action={
        <Box
          component="button"
          onClick={() => navigate('/settings#group.defaults.group-payroll')}
          sx={{
            background: 'none',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: '0.875rem',
            mr: 1,
          }}
        >
          Manage
        </Box>
      }
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <AlertTitle sx={{ m: 0, fontSize: '0.875rem' }}>{title}</AlertTitle>
        {inboxBadges.map((b) => (
          <Chip
            key={b.label}
            size="small"
            label={`${b.count} ${b.label}`}
            color={severity === 'warning' ? 'warning' : 'info'}
          />
        ))}
        {ctx.role === 'paymaster' &&
          ctx.operated_companies.some((o) => o.is_in_cooloff) && (
            <Chip size="small" label="cool-off in progress" color="warning" />
          )}
      </Stack>
    </Alert>
  );
}
